import { fetchAllPages } from "@/api/page";
import { listAnnotationSetFiles, type AnnotationSetFileView } from "@/api/trainingAnnotation";
import {
  fetchStorageBody,
  type DownloadFileTransport,
  type RunnableDownloadEntry,
} from "@/components/dataset/download";
import { isHttpClientError } from "@/http";
import { parseUtcMs } from "@/utils/datetime";

/**
 * 렌더가 끝난 Annotation Set 의 keyframe PNG/YAML 을 사용자가 고른 폴더로 받는 경계.
 *
 * 받은 파일은 `annotation/random_background/<camera>/<file>` 에 둔다. learning-core
 * `RandomBackground` 의 기본 폴더라 Dataset 폴더에 받으면 그대로 학습 입력이 된다.
 * 바이트는 BE 가 주는 signed URL 로 storage 에서 직접 받는다(`docs/rules.md` §7).
 */

export const ANNOTATION_SET_DOWNLOAD_ROOT = "annotation/random_background";

// 목록 조회도 사용자가 누른 단발 bulk 조작이라 카탈로그 기본보다 넓게 연다.
const LIST_PAGE_OPTIONS = { concurrency: 6 } as const;

// 이 여유 안에 만료되는 URL 은 재서명 캐시로 쓰지 않는다(러너의 만료 여유와 같다).
const EXPIRY_MARGIN_MS = 60_000;

export type AnnotationSetDownloadEntry = RunnableDownloadEntry & {
  // 세트 폴더 기준 경로(`<camera>/<file>`). 재서명 때 목록에서 같은 파일을 찾는 키다.
  setRelativePath: string;
};

export class AnnotationFolderExistsError extends Error {
  constructor() {
    super("annotation/random_background already exists in the chosen folder");
    this.name = "AnnotationFolderExistsError";
  }
}

export function toAnnotationSetDownloadEntries(
  files: readonly AnnotationSetFileView[],
): AnnotationSetDownloadEntry[] {
  return files.map((file) => ({
    setRelativePath: file.relative_path,
    relativePath: `${ANNOTATION_SET_DOWNLOAD_ROOT}/${file.relative_path}`,
    sizeBytes: file.size_bytes,
    url: file.url,
    expiresAt: file.expires_at,
  }));
}

/** 세트 파일 전체를 페이지로 모은다. 페이지마다 서명 URL 이 함께 온다. */
export function collectAnnotationSetFiles(
  setUuid: string,
  signal: AbortSignal,
): Promise<AnnotationSetFileView[]> {
  return fetchAllPages(
    (query) => listAnnotationSetFiles(setUuid, query, { signal }),
    LIST_PAGE_OPTIONS,
  );
}

/**
 * 고른 폴더에 이미 `annotation/random_background` 가 있으면 받지 않는다.
 *
 * writer 는 파일을 지우지 못해서, 다른 세트나 업로드 번들이 있던 자리에 받으면 두 세트가
 * 한 폴더에 섞인다. 섞인 폴더는 학습 입력으로 조용히 들어가므로 시작 전에 막는다.
 */
export async function assertNoAnnotationFolder(root: FileSystemDirectoryHandle): Promise<void> {
  const [parent, child] = ANNOTATION_SET_DOWNLOAD_ROOT.split("/");
  try {
    const annotation = await root.getDirectoryHandle(parent);
    await annotation.getDirectoryHandle(child);
  } catch (error) {
    if (error instanceof DOMException && error.name === "NotFoundError") return;
    throw error;
  }
  throw new AnnotationFolderExistsError();
}

/**
 * 러너가 쓰는 transport 다. 목록이 이미 서명했으므로 창 서명은 그대로 통과시킨다.
 *
 * 재서명은 목록을 한 번만 다시 받는다. TTL 이 지나면 남은 파일이 한꺼번에 재서명을 요청한다.
 * 파일마다 목록을 다시 받으면 파일 수 × 페이지 수만큼 서명한다.
 */
export function createAnnotationSetTransport(
  setUuid: string,
): DownloadFileTransport<AnnotationSetDownloadEntry> {
  let cached: Map<string, AnnotationSetFileView> | null = null;
  let inFlight: Promise<Map<string, AnnotationSetFileView>> | null = null;

  function refresh(signal: AbortSignal): Promise<Map<string, AnnotationSetFileView>> {
    if (cached !== null && allUsable(cached)) return Promise.resolve(cached);
    inFlight ??= collectAnnotationSetFiles(setUuid, signal)
      .then((files) => {
        cached = new Map(files.map((file) => [file.relative_path, file]));
        return cached;
      })
      .finally(() => {
        inFlight = null;
      });
    return inFlight;
  }

  return {
    async signWindow(entries) {
      return entries.map((entry) => ({ ...entry, unavailableReason: null }));
    },
    async resign(entry, signal) {
      const files = await refresh(signal);
      const file = files.get(entry.setRelativePath);
      if (file === undefined) {
        throw new Error(`annotation set file is no longer listed: ${entry.setRelativePath}`);
      }
      return file.url;
    },
    fetchBody: fetchStorageBody,
  };
}

function allUsable(files: Map<string, AnnotationSetFileView>): boolean {
  const now = Date.now();
  for (const file of files.values()) {
    const expiresAt = parseUtcMs(file.expires_at);
    if (expiresAt === null || expiresAt - now <= EXPIRY_MARGIN_MS) return false;
  }
  return true;
}

/** 시작 전 실패를 사용자에게 보여 줄 문장으로 바꾼다. */
export function annotationSetDownloadErrorMessage(error: unknown): string {
  if (error instanceof AnnotationFolderExistsError) {
    return "고른 폴더에 annotation/random_background 폴더가 이미 있습니다. 빈 폴더를 고르거나 기존 폴더를 옮긴 뒤 다시 시도해 주세요.";
  }
  if (isHttpClientError(error)) {
    switch (error.code) {
      case "training.annotation_set_not_committed":
      case "training.annotation_set_not_materialized":
        return "렌더가 끝난 Annotation Set만 내려받을 수 있습니다.";
      case "training.annotation_set_not_found":
        return "Annotation Set을 찾을 수 없습니다. 목록을 새로고침해 주세요.";
      case "training.annotation_set_files_unavailable":
        return "Annotation Set 파일을 저장소에서 찾지 못했습니다. 관리자에게 문의해 주세요.";
      case "training.annotation_set_files_too_large":
        return "파일이 너무 많은 Annotation Set이라 내려받을 수 없습니다. 관리자에게 문의해 주세요.";
    }
  }
  return "Annotation Set 파일을 내려받지 못했습니다. 잠시 후 다시 시도해 주세요.";
}
